import { AirlineCode, AirwaySeatLocation, Flight, SegmentType } from "~/application/types";
import { DateFormats, format, timestampInterval } from "~/application/utils/date-functions";
import { FlightSegmentInfo } from "./types";
import { AirwayFlightQuery } from "~/presentation/Booking/BookingAirway/pages/FlightsPage/hooks/useAirwayReducer/types";
import { AirwaySeatsDTO, AirwaySeatsPerPassengerDTO } from "~/infrastructure/api/dtos";

const AIRLINE_NAMES: Record<AirlineCode, string> = {
  [AirlineCode.AZUL]: "Azul",
  [AirlineCode.AMERICAN_AIRLINES]: "American Airlines",
  [AirlineCode.GOL]: "Gol",
  [AirlineCode.LATAM]: "Latam",
  [AirlineCode.VOEPASS]: "Voepass",
  [AirlineCode.XL_AIRWAYS_FRANCE]: "XL Airways France",
  [AirlineCode.UNITED_AIRLINES]: "United Airlines",
  [AirlineCode.TURKISH_AIRLINES]: "Turkish Airlines",
  [AirlineCode.KLM]: "KLM",
  [AirlineCode.IBERIA]: "Iberia",
  [AirlineCode.AIR_FRANCE]: "Air France",
  [AirlineCode.TAP]: "TAP",
  [AirlineCode.AT]: "AT",
  [AirlineCode.LUFTHANSA]: "Lufthansa",
  [AirlineCode.INTERNATIONAL_AMERICAN_AIRLINES]: "American Airlines",
  [AirlineCode.AIR_CANADA]: "Air Canada",
  [AirlineCode.BRITISH_AIRWAYS]: "British Airways",
  [AirlineCode.DELTA_AIRLINES]: "Delta Airlines",
  [AirlineCode.AVIANCA]: "Avianca",
};


export function parseSeatLocation(raw: string): AirwaySeatLocation {
  const normalized = (raw || "").trim().toLowerCase();
  if (normalized === "window" || normalized === "w") {
    return AirwaySeatLocation.WINDOW;
  }
  if (normalized === "aisle" || normalized === "a") {
    return AirwaySeatLocation.AISLE;
  }
  return AirwaySeatLocation.AISLE;
}

export function isPerPassengerPayload(
  data: AirwaySeatsDTO["data"],
): data is AirwaySeatsPerPassengerDTO[] {
  if (!data?.length) {
    return false;
  }
  const first = data[0] as Record<string, unknown>;
  return (
    typeof first.customer_employee_uuid === "string" &&
    (Array.isArray(first.stretchs) || Array.isArray(first.stretches))
  );
}

export function formatFlightInfo(
  flight: Flight,
  isInternationalCombined?: boolean,
  flightQuery?: AirwayFlightQuery
): FlightSegmentInfo {
  const departureInfo = flight.segments[0].departureFlightInfo;
  const arrivalInfo = flight.segments[flight.segments.length - 1].arrivalFlightInfo;

  const connectionsAirportIatas = flight.segments
    .slice(0, -1)
    .map((segment) => segment.arrivalFlightInfo.airportIata);

  const intermediateSegments = flight.segments.slice(1, -1);

  const departureConnectionIataCodes = intermediateSegments
    .filter((segment) => segment.type === SegmentType.DEPARTURE)
    .map((segment) => segment.departureFlightInfo.airportIata);

  const arrivalConnectionIataCodes = intermediateSegments
    .filter((segment) => segment.type === SegmentType.ARRIVAL)
    .map((segment) => segment.arrivalFlightInfo.airportIata);

  return {
    flight,
    flightDuration: timestampInterval(departureInfo.dateTime, arrivalInfo.dateTime),
    departureFlightInfo: {
      airportIata: departureInfo.airportIata,
      airport: departureInfo.airport,
      dateTimeHour12: format(departureInfo.dateTime, DateFormats.SMALL_TIME_AM_PM),
      date: format(departureInfo.dateTime, DateFormats.LONG_DATE),
      connectionIataCodes: departureConnectionIataCodes,
    },
    arrivalInfo: {
      airportIata: arrivalInfo.airportIata,
      airport: arrivalInfo.airport,
      dateTimeHour12: format(arrivalInfo.dateTime, DateFormats.SMALL_TIME_AM_PM),
      date: format(arrivalInfo.dateTime, DateFormats.LONG_DATE),
      connectionIataCodes: arrivalConnectionIataCodes,
    },
    connectionsAirportIatas,
  };
}

const AIRLINE_IATA_CODES: Record<string, string> = {
  gws: "G3",
  latam: "LA",
  azul: "AD",
  aa: "AA",
  "2z": "2Z",
  xl: "SE",
  "united-airlines": "UA",
  "turkish-airlines": "TK",
  klm: "KL",
  iberia: "IB",
  "air-france": "AF",
  tap: "TP",
  at: "AT",
  lufthansa: "LH",
  "american-airlines": "AA",
  "air-canada": "AC",
  "british-airways": "BA",
  "delta-airlines": "DL",
  avianca: "AV",
};

export function getAirlineIataCode(airline: AirlineCode | string): string {
  if (!airline) return "";
  const key = airline.toString().toLowerCase();
  return AIRLINE_IATA_CODES[key] ?? airline.toUpperCase();
}

export function getAirlineUrl(airline: AirlineCode | string, iataOverride?: string | null) {
  if (iataOverride && iataOverride.length === 2) {
    return `https://pics.avs.io/160/80/${iataOverride.toUpperCase()}.png`;
  }

  const airlineToUse = iataOverride && iataOverride.length > 2 ? iataOverride : airline;
  if (!airlineToUse) return "";

  const key = airlineToUse.toString().toLowerCase();

  if (AIRLINE_IATA_CODES[key]) {
    return `https://pics.avs.io/160/80/${AIRLINE_IATA_CODES[key]}.png`;
  }

  if (key.length === 2) {
    return `https://pics.avs.io/160/80/${airlineToUse.toString().toUpperCase()}.png`;
  }

  // Otherwise, fall back to the local image folder, which is what the domestic flow expects
  return `/images/airlines/${key}.png`;
}

export function getAirlineName(airline: AirlineCode): string {
  return AIRLINE_NAMES[airline] ?? airline;
}

export const flightDuration = (item: Flight) => {
  const goFlight = item?.segments?.[0]?.departureFlightInfo;
  const returnFlight = item?.segments?.[item.segments.length - 1]?.arrivalFlightInfo;

  if (goFlight?.dateTime && returnFlight?.dateTime) {
    return Math.abs(goFlight.dateTime.getTime() - returnFlight.dateTime.getTime());
  }

  return 0;
};

const legDuration = (segments: Flight["segments"], leg: "outbound" | "inbound") => {
  const inboundStartIndex = segments.findIndex((segment) => segment.type === SegmentType.ARRIVAL);
  const hasRoundTrip = inboundStartIndex > 0;

  const startIndex = leg === "inbound" && hasRoundTrip ? inboundStartIndex : 0;
  const endIndex = leg === "outbound" && hasRoundTrip ? inboundStartIndex - 1 : segments.length - 1;

  const firstDeparture = segments[startIndex]?.departureFlightInfo?.dateTime;
  const lastArrival = segments[endIndex]?.arrivalFlightInfo?.dateTime;

  if (!firstDeparture || !lastArrival) {
    return 0;
  }

  return Math.abs(lastArrival.getTime() - firstDeparture.getTime());
};

export const outboundFlightDuration = (item: Flight) =>
  legDuration(item?.segments ?? [], "outbound");

export const inboundFlightDuration = (item: Flight) => legDuration(item?.segments ?? [], "inbound");

export const totalFlightDuration = (item: Flight) => {
  const segments = item?.segments ?? [];
  const inboundStartIndex = segments.findIndex((segment) => segment.type === SegmentType.ARRIVAL);
  const hasRoundTrip = inboundStartIndex > 0;

  if (hasRoundTrip) {
    return outboundFlightDuration(item) + inboundFlightDuration(item);
  }

  return outboundFlightDuration(item);
};

export const flightsDuration = (data: Flight[]) => {
  const flightsDurationData = data.map(flightDuration);

  const maxDuration = flightsDurationData.reduce((acc, value) => {
    return value >= acc ? value : acc;
  }, 0);

  const minDuration = flightsDurationData.reduce((acc, value, index) => {
    if (index === 0) return value;
    return value < acc && value > 0 ? value : acc;
  }, 0);

  return {
    max: maxDuration,
    min: minDuration,
  };
};

export function millisecondsToHours(milliseconds: number): string {
  const millisecondsPerHours = 3600000;
  const millisecondsPerMinute = 60000;

  const hour = Math.floor(milliseconds / millisecondsPerHours);
  const minutesRemaining = milliseconds % millisecondsPerHours;
  const minutes = Math.floor(minutesRemaining / millisecondsPerMinute);

  return `${hour}h ${minutes}m`;
}
